import "./AppSelectV2.scss";

import React, { useEffect, useMemo, useRef, useState } from "react";

const isBlank = (v) => v == null || String(v).trim() === "";

function AppSelectV2({
  name,
  label = "Selecione",
  placeholder = "Selecione",
  isRequired = false,

  options = [],
  value,
  onChange,

  errors = {},
  touched = false,

  disabled = false,
  readOnly = false,

  leftIcon,
}) {
  const rootRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const hasError = !!errors?.[name] && !!touched;

  // Fecha ao clicar fora
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // quando abrir, reseta pesquisa
  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const selectedLabel = useMemo(() => {
    if (!value) return "";
    return value?.label ?? value?.name ?? "";
  }, [value]);

  const filteredOptions = useMemo(() => {
    if (isBlank(search)) return options;

    const txt = String(search).toLowerCase();
    return (options || []).filter((opt) => {
      const labelTxt = String(opt?.label ?? opt?.name ?? "").toLowerCase();
      return labelTxt.includes(txt);
    });
  }, [options, search]);

  function handleToggleOpen() {
    if (disabled || readOnly) return;
    setOpen((s) => !s);
  }

  function handleSelect(opt) {
    onChange?.(opt);
    setOpen(false);
  }

  function handleClear(e) {
    e.stopPropagation();
    onChange?.(null);
    setOpen(false);
  }

  return (
    <div
      ref={rootRef}
      className={`hallapp-selectv2 ${disabled ? "is-disabled" : ""} ${hasError ? "is-error" : ""}`}
    >
      {/* Label em cima */}
      {label && (
        <div className="hallapp-selectv2__label">
          <span>{label}</span>
          {isRequired && <span className="hallapp-selectv2__required"> *(obrigatório)</span>}
        </div>
      )}

      {/* Campo */}
      <div
        className={`hallapp-selectv2__wrap ${open ? "is-open" : ""} ${readOnly ? "is-readonly" : ""}`}
        onClick={handleToggleOpen}
        role="button"
        tabIndex={0}
      >
        {leftIcon && <div className="hallapp-selectv2__iconLeft">{leftIcon}</div>}

        <div className={`hallapp-selectv2__value ${!value ? "is-empty" : ""}`}>
          {value ? selectedLabel : placeholder}
        </div>

        <div className="hallapp-selectv2__right">
          {value && !readOnly && !disabled ? (
            <button
              type="button"
              className="hallapp-selectv2__clearBtn"
              onClick={handleClear}
              aria-label="Limpar seleção"
            >
              ✕
            </button>
          ) : null}

          <div className="hallapp-selectv2__chevron">{open ? "▲" : "▼"}</div>
        </div>
      </div>

      {/* Erro */}
      <div className="hallapp-selectv2__meta">
        {hasError ? <div className="hallapp-selectv2__error">{errors?.[name]}</div> : <span />}
      </div>

      {/* Dropdown */}
      {open && (
        <div className="hallapp-selectv2__dropdown">
          <div className="hallapp-selectv2__searchWrap">
            <input
              className="hallapp-selectv2__search"
              placeholder="Pesquisar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          <div className="hallapp-selectv2__list">
            {filteredOptions.length === 0 ? (
              <div className="hallapp-selectv2__empty">Nenhum resultado encontrado</div>
            ) : (
              filteredOptions.map((opt, idx) => {
                const optLabel = opt?.label ?? opt?.name ?? "";
                const selected = value?.value === opt?.value;

                return (
                  <div
                    key={`${opt?.value}-${idx}`}
                    className={`hallapp-selectv2__item ${selected ? "is-selected" : ""}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(opt)}
                  >
                    <div className="hallapp-selectv2__itemTitle">{optLabel}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AppSelectV2;
